import { ref, computed } from 'vue';

export interface MattersFilters {
  search: string;
  archived: boolean;

  // Matters Involving
  scopeAccount: { id: string } | string | null;
  originating: boolean;
  involved: boolean;
  primary: boolean;
  reviewer: boolean;

  // Client filter - narrows to matters belonging to a single client
  client: { id: string } | string | null;

  // QuickBooks connection validation (opt-in - expensive per-matter check)
  checkQuickbooks: boolean;
  qbConnectionType: string;

  // Custom field filters: fieldId -> optionId (or 'NOT_ASSIGNED'). Exclusive
  // per field - a matter only ever holds one value per custom field.
  matterCustomFields: Record<string, string>;

  // Paging / sort
  page: number;
  pageSize: number;
  orderBy: string;
  sortAscending: boolean;
}

const getDefaultFilters = (): MattersFilters => ({
  search: '',
  archived: false,

  scopeAccount: null,
  originating: false,
  involved: false,
  primary: false,
  reviewer: false,

  client: null,

  checkQuickbooks: false,
  qbConnectionType: 'any',

  matterCustomFields: {},

  page: 1,
  pageSize: 20,
  // 'matterId' is the backend's recognized orderBy value for this column
  // (MatterRepo.GetOrderBy); it happens to fall through to the same SQL as
  // its unmatched default case, but 'matterId' is the real, documented one.
  orderBy: 'matterId',
  sortAscending: true,
});

export function useMattersFilters() {
  const filters = ref<MattersFilters>(getDefaultFilters());

  const activeFilterCount = computed(() => {
    const f = filters.value;
    return [
      f.search,
      f.archived,
      f.scopeAccount,
      f.client,
      f.qbConnectionType !== 'any' && f.checkQuickbooks,
      Object.keys(f.matterCustomFields || {}).length > 0,
    ].filter(Boolean).length;
  });

  const queryParams = computed(() => {
    const f = filters.value;
    const params: Record<string, unknown> = {
      limit: f.pageSize,
      offset: f.page - 1,
      orderBy: f.orderBy,
      sortAscending: f.sortAscending,
      archived: f.archived,
    };

    if (f.search) params.search = f.search;

    if (f.scopeAccount) {
      params.scopeAccount = typeof f.scopeAccount === 'string' ? f.scopeAccount : f.scopeAccount.id;
      params.originating = f.originating;
      params.primary = f.primary;
      params.reviewer = f.reviewer;
      params.involved = f.involved;
    }

    if (f.checkQuickbooks) {
      params.checkQuickbooks = true;
      params.qbConnectionType = f.qbConnectionType;
    }

    if (f.client) {
      params.clientId = typeof f.client === 'string' ? f.client : f.client.id;
    }

    // Backend binds these as List<CustomFieldFilter> (MatterTableFilter ->
    // ScopeableFilter.MatterCustomField) via indexed query keys; omitting
    // optionId for 'NOT_ASSIGNED' matches how MatterRepo's
    // BuildCustomFieldWhereClause treats a missing option as "no value set".
    Object.entries(f.matterCustomFields || {})
      .filter(([, optionId]) => optionId)
      .forEach(([fieldId, optionId], index) => {
        params[`matterCustomField[${index}].fieldId`] = fieldId;
        if (optionId !== 'NOT_ASSIGNED') {
          params[`matterCustomField[${index}].optionId`] = optionId;
        }
      });

    return params;
  });

  const resetFilters = () => {
    filters.value = getDefaultFilters();
  };

  return {
    filters,
    activeFilterCount,
    queryParams,
    resetFilters,
  };
}
